import { Button, notify } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { ResearchAreaPicker } from '@affine/core/components/researcher-onboarding/research-area-picker';
import { OrchestrationService } from '@affine/core/modules/orchestration';
import type { WorkspaceContext } from '@affine/core/modules/orchestration/types';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import * as styles from './style.css';

type CitationStyle = NonNullable<WorkspaceContext['citationStyle']>;
type ResponseStyle = NonNullable<WorkspaceContext['responseStyle']>;

const CITATION_STYLES: CitationStyle[] = [
  'apa',
  'mla',
  'chicago',
  'vancouver',
  'nature',
];
const RESPONSE_STYLES: ResponseStyle[] = [
  'concise',
  'detailed',
  'technical',
  'simplified',
];

/**
 * Workspace › Research Context settings panel.
 *
 * Surfaces the workspace's AI research context (researchFocus,
 * researchGoal, keyTerms, citation/response style) so a researcher can
 * inspect and change what the AI knows about the project after
 * creation — previously these were seeded once at workspace creation
 * and then locked with no UI. Every Explore / Analyze / Deep Research
 * run picks up the saved values.
 *
 * Loads via `OrchestrationService.getWorkspaceContext` and persists via
 * `updateWorkspaceContext` (both already implemented on the service).
 * Rendered as the `workspace:research-context` tab in the settings
 * modal — no standalone route.
 */
export const WorkspaceSettingResearchContext = () => {
  const workspaceService = useService(WorkspaceService);
  const orchestration = useService(OrchestrationService);
  const workspaceId = workspaceService.workspace.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [researchGoal, setResearchGoal] = useState('');
  const [researchFocus, setResearchFocus] = useState('');
  const [keyTerms, setKeyTerms] = useState<string[]>([]);
  const [citationStyle, setCitationStyle] = useState<CitationStyle | ''>('');
  const [responseStyle, setResponseStyle] = useState<ResponseStyle | ''>('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    orchestration
      .getWorkspaceContext(workspaceId)
      .then(ctx => {
        if (cancelled || !ctx) return;
        setResearchGoal(ctx.researchGoal ?? '');
        setResearchFocus(ctx.researchFocus ?? '');
        setKeyTerms(ctx.keyTerms ?? []);
        setCitationStyle(ctx.citationStyle ?? '');
        setResponseStyle(ctx.responseStyle ?? '');
      })
      .catch(err => {
        console.error('Failed to load workspace context', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orchestration, workspaceId]);

  const handleSave = useAsyncCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      await orchestration.updateWorkspaceContext(workspaceId, {
        researchGoal: researchGoal.trim() || undefined,
        researchFocus: researchFocus.trim() || undefined,
        keyTerms,
        citationStyle: citationStyle || undefined,
        responseStyle: responseStyle || undefined,
      });
      notify.success({ title: 'Research context saved' });
    } catch (err) {
      console.error('Failed to save workspace context', err);
      notify.error({
        title: 'Could not save research context',
        message: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  }, [
    citationStyle,
    keyTerms,
    orchestration,
    researchFocus,
    researchGoal,
    responseStyle,
    saving,
    workspaceId,
  ]);

  return (
    <>
      <SettingHeader
        title="Research Context"
        subtitle="What the AI knows about this workspace. Used to focus every Explore, Analyze, and Deep Research run."
      />

      <SettingWrapper title="Project">
        <SettingRow
          name="Research focus"
          desc="A short phrase describing what this workspace investigates."
          spreadCol={false}
        >
          <input
            className={styles.input}
            value={researchFocus}
            placeholder="e.g. tau aggregation kinetics in iPSC-derived neurons"
            disabled={loading}
            onChange={e => setResearchFocus(e.target.value)}
          />
        </SettingRow>

        <SettingRow
          name="Research goal"
          desc="What you are ultimately trying to find out."
          spreadCol={false}
        >
          <textarea
            className={styles.textarea}
            value={researchGoal}
            placeholder="e.g. Identify a selective inhibitor that halts tau aggregation without off-target toxicity."
            maxLength={1000}
            disabled={loading}
            onChange={e => setResearchGoal(e.target.value)}
          />
        </SettingRow>

        <SettingRow
          name="Key terms"
          desc="Domain keywords that bias literature search and citation ranking."
          spreadCol={false}
        >
          <div className={styles.picker}>
            <ResearchAreaPicker value={keyTerms} onChange={setKeyTerms} />
          </div>
        </SettingRow>
      </SettingWrapper>

      <SettingWrapper title="AI output">
        <SettingRow
          name="Citation style"
          desc="Format used when the AI renders references."
        >
          <select
            className={styles.select}
            value={citationStyle}
            disabled={loading}
            onChange={e =>
              setCitationStyle(e.target.value as CitationStyle | '')
            }
          >
            <option value="">Default</option>
            {CITATION_STYLES.map(s => (
              <option key={s} value={s}>
                {s.toUpperCase()}
              </option>
            ))}
          </select>
        </SettingRow>

        <SettingRow
          name="Response style"
          desc="How detailed the AI's answers should be."
        >
          <select
            className={styles.select}
            value={responseStyle}
            disabled={loading}
            onChange={e =>
              setResponseStyle(e.target.value as ResponseStyle | '')
            }
          >
            <option value="">Default</option>
            {RESPONSE_STYLES.map(s => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </SettingRow>
      </SettingWrapper>

      <SettingWrapper>
        <SettingRow
          name="Save changes"
          desc="Apply the updated context to this workspace."
        >
          <Button
            variant="primary"
            loading={saving}
            disabled={loading}
            onClick={handleSave}
          >
            Save
          </Button>
        </SettingRow>
      </SettingWrapper>
    </>
  );
};
